import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  resendSignUpCode,
  fetchAuthSession,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { generateClient } from "aws-amplify/data";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import skillbubbleLogoWhite from "../../assets/images/logo_sb-white.25.svg";
import sbLogo from "../../assets/images/logo_sb-blue.25.svg";
import type { AuthPageProps } from "../../types/ui";
import type { Schema } from "@backend-schema";
import { addCommunityPackage } from "../../utils/packagePublishService";
import "./AuthPage.css";

type AuthView =
  | "signIn"
  | "signUp"
  | "confirmSignUp"
  | "confirmMFA"
  | "forgotPassword"
  | "confirmResetPassword";

interface AuthBrandingSectionProps {
  t: (key: string) => string;
}

const AuthBrandingSection: React.FC<AuthBrandingSectionProps> = ({ t }) => (
  <div className="auth-branding-modern">
    <div className="branding-content-modern">
      <img
        className="branding-logo-modern"
        src={skillbubbleLogoWhite}
        alt="SkillBubble"
      />
      <div className="branding-title-modern">{t("branding.title")}</div>
      <div className="branding-subtitle-modern">{t("branding.subtitle")}</div>
      <div className="branding-features-modern">
        <div className="feature-item-modern">
          <div className="feature-icon-modern">✓</div>
          <span>{t("branding.features.unlimitedSkills")}</span>
        </div>
        <div className="feature-item-modern">
          <div className="feature-icon-modern">✓</div>
          <span>{t("branding.features.qrCodes")}</span>
        </div>
        <div className="feature-item-modern">
          <div className="feature-icon-modern">✓</div>
          <span>{t("branding.features.certifications")}</span>
        </div>
        <div className="feature-item-modern">
          <div className="feature-icon-modern">✓</div>
          <span>{t("branding.features.aiInsights")}</span>
        </div>
      </div>
      <div className="branding-stat-modern">
        <div className="stat-number-modern">{t("branding.stat.number")}</div>
        <div className="stat-label-modern">{t("branding.stat.label")}</div>
      </div>
      <Link to="/" className="branding-back-modern">
        {t("branding.backToHome")}
      </Link>
    </div>
  </div>
);

interface SocialButtonsProps {
  t: (key: string) => string;
}

const SocialButtons: React.FC<SocialButtonsProps> = ({ t }) => {

  return (
    <>
      <div className="social-buttons-modern">
        
        <button className="social-btn-modern social-btn-disabled" type="button" disabled aria-label={`Sign in with ${t("social.github")} - Coming soon`}>
          <svg viewBox="0 0 24 24" fill="#333">
            <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.604-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.161 22 16.416 22 12c0-5.523-4.477-10-10-10z" />
          </svg>
          <span className="social-btn-label">{t("social.github")}</span>
          <span className="social-btn-coming-soon">Coming soon</span>
        </button>
        <button className="social-btn-modern social-btn-disabled" type="button" disabled aria-label={`Sign in with ${t("social.apple")} - Coming soon`}>
          <svg viewBox="0 0 24 24" fill="#000">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          <span className="social-btn-label">{t("social.apple")}</span>
          <span className="social-btn-coming-soon">Coming soon</span>
        </button>
      </div>
      <div className="auth-divider-modern">
        <span>{t("social.divider")}</span>
      </div>
    </>
  );
};

/* ===== Password visibility toggle icon ===== */
const EyeIcon: React.FC<{ visible: boolean }> = ({ visible }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {visible ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

/* ===== Password input with toggle ===== */
interface PasswordFieldProps {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (val: string) => void;
  error?: string;
  required?: boolean;
  autoComplete?: string;
  disabled?: boolean;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  id, label, placeholder, value, onChange, error, required = true, autoComplete = "current-password", disabled = false,
}) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="auth-form-group-modern">
      <label className="auth-form-label-modern" htmlFor={id}>
        {label}{required && <span className="required">*</span>}
      </label>
      <div className="password-wrapper-modern">
        <input
          id={id}
          className={`auth-form-input-modern${error ? " error" : ""}`}
          type={visible ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          disabled={disabled}
        />
        <button
          type="button"
          className="password-toggle-modern"
          onClick={() => setVisible(!visible)}
          aria-label={visible ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          <EyeIcon visible={visible} />
        </button>
      </div>
      {error && <div id={`${id}-error`} className="auth-form-error-modern" role="alert">{error}</div>}
    </div>
  );
};

/* ===== Safe redirect helper ===== */
const SAFE_REDIRECT_PREFIXES = ["/app", "/mcp-auth"];

const getSafeRedirect = (next: string | null): string | null => {
  if (!next) return null;
  try {
    const decoded = decodeURIComponent(next);
    // Only allow relative paths starting with / — block absolute URLs, protocol-relative, and backslash
    if (!decoded.startsWith("/") || decoded.startsWith("//") || decoded.includes("\\")) {
      return null;
    }
    // Block javascript: and data: URIs that could be encoded within the path
    if (/javascript:|data:/i.test(decoded)) {
      return null;
    }
    // Only allow paths under known safe prefixes
    if (!SAFE_REDIRECT_PREFIXES.some(prefix => decoded.startsWith(prefix))) {
      return null;
    }
    return decoded;
  } catch {
    return null;
  }
};

/* ===== Auth Method Selector ===== */
interface AuthMethodSelectorProps {
  method: 'password' | 'emailCode';
  onChange: (method: 'password' | 'emailCode') => void;
}

const AuthMethodSelector: React.FC<AuthMethodSelectorProps> = ({ method, onChange }) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      onChange(method === 'password' ? 'emailCode' : 'password');
    }
  };

  return (
    <div className="auth-method-selector" role="tablist" aria-label="Authentication method">
      <button
        type="button"
        role="tab"
        className="method-tab"
        aria-selected={method === 'password'}
        tabIndex={method === 'password' ? 0 : -1}
        onClick={() => onChange('password')}
        onKeyDown={handleKeyDown}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
        Password
      </button>
      <button
        type="button"
        role="tab"
        className="method-tab"
        aria-selected={method === 'emailCode'}
        tabIndex={method === 'emailCode' ? 0 : -1}
        onClick={() => onChange('emailCode')}
        onKeyDown={handleKeyDown}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
        Email Code
      </button>
    </div>
  );
};

/* ===== Magic Link Sign In Form ===== */
interface MagicLinkSignInFormProps {
  t: (key: string) => string;
  onViewChange: (view: AuthView, email?: string) => void;
}

const MagicLinkSignInForm: React.FC<MagicLinkSignInFormProps> = ({ t, onViewChange }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<'email' | 'code'>('email');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");

  const validateEmail = (): boolean => {
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = t("fields.email.label") + " is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Enter a valid email address";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const initiateSignIn = async () => {
    setError("");
    setStatusMessage("");
    if (!validateEmail()) return;
    setLoading(true);
    try {
      const result = await signIn({ username: email.trim(), options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        setPhase('code');
        setCode("");
        setFailedAttempts(0);
        setStatusMessage("A verification code has been sent to your email.");
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        onViewChange('confirmMFA', email.trim());
      } else if (result.isSignedIn) {
        await fetchAuthSession();
        const { invalidateUserAttributesCache } = await import('../../utils/userAttributesCache');
        invalidateUserAttributesCache();
        await new Promise(resolve => setTimeout(resolve, 100));
        const params = new URLSearchParams(window.location.search);
        const safeRedirect = getSafeRedirect(params.get("next"));
        navigate(safeRedirect || "/app", { replace: true });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Sign in failed";
      if (message.includes("User does not exist") || message.includes("UserNotFoundException")) {
        setError("No account found with this email. Would you like to sign up?");
      } else if (message.includes("RATE_LIMITED")) {
        setError("Too many code requests. Please wait a few minutes and try again.");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await initiateSignIn();
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setStatusMessage("");
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        onViewChange('confirmMFA', email.trim());
      } else if (result.isSignedIn) {
        await fetchAuthSession();
        const { invalidateUserAttributesCache } = await import('../../utils/userAttributesCache');
        invalidateUserAttributesCache();
        await new Promise(resolve => setTimeout(resolve, 100));
        const params = new URLSearchParams(window.location.search);
        const safeRedirect = getSafeRedirect(params.get("next"));
        navigate(safeRedirect || "/app", { replace: true });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Verification failed";
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (newAttempts >= 3) {
        setError("Too many incorrect attempts. Please request a new code.");
        setPhase('email');
        setCode("");
        setFailedAttempts(0);
      } else if (message.includes("Invalid") || message.includes("NotAuthorizedException") || message.includes("CodeMismatch")) {
        setError("Invalid code. Please check and try again.");
      } else if (message.includes("expired")) {
        setError("Code expired. Please request a new code.");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError("");
    setResent(false);
    setStatusMessage("");
    try {
      // Sign out first to clear any existing session, then start fresh
      try { await signOut(); } catch { /* ignore if not signed in */ }
      const result = await signIn({ username: email.trim(), options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        setCode("");
        setFailedAttempts(0);
        setResent(true);
        setStatusMessage("A new code has been sent to your email. Previous codes are now invalid.");
        setTimeout(() => setResent(false), 5000);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to resend code";
      if (message.includes("RATE_LIMITED")) {
        setError("Too many code requests. Please wait a few minutes and try again.");
      } else {
        setError(message);
      }
    } finally {
      setResending(false);
    }
  };

  if (phase === 'code') {
    return (
      <form onSubmit={handleCodeSubmit} noValidate>
        {error && (
          <div className="auth-alert-modern auth-alert-error-modern" role="alert">{error}</div>
        )}
        {resent && (
          <div className="auth-alert-modern auth-alert-success-modern" role="status" aria-live="polite">
            A new code has been sent to {email}
          </div>
        )}
        {statusMessage && !resent && (
          <div role="status" aria-live="polite" className="auth-mfa-info-modern">{statusMessage}</div>
        )}
        <p className="auth-mfa-info-modern">Enter the 6-digit code sent to {email}</p>
        <CodeInputGroup value={code} onChange={setCode} id="magic-code" disabled={loading} />
        <button type="submit" className="auth-btn-primary-modern" disabled={loading || code.length !== 6}>
          {loading ? "Verifying..." : "Verify & Sign In"}
        </button>
        <div className="magic-link-resend-modern">
          <button type="button" className="resend-link-modern" onClick={handleResend} disabled={resending}>
            {resending ? "Sending..." : "Resend code"}
          </button>
        </div>
        <div className="auth-switch-text-modern">
          {t("signIn.switchText")}{" "}
          <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signUp")}>
            {t("signIn.switchLink")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleEmailSubmit} noValidate>
      {error && (
        <div className="auth-alert-modern auth-alert-error-modern" role="alert">
          {error}
          {error.includes("sign up") && (
            <>
              {" "}
              <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signUp", email.trim())}>
                Sign up
              </button>
            </>
          )}
        </div>
      )}
      <div className="auth-form-group-modern">
        <label className="auth-form-label-modern" htmlFor="magic-signIn-email">
          {t("fields.email.label")}<span className="required">*</span>
        </label>
        <input
          id="magic-signIn-email"
          className={`auth-form-input-modern${fieldErrors.email ? " error" : ""}`}
          type="email"
          placeholder={t("fields.email.placeholder")}
          value={email}
          onChange={(e) => { setEmail(e.target.value); setFieldErrors((p) => ({ ...p, email: "" })); }}
          required
          autoComplete="email"
          autoFocus
          aria-invalid={!!fieldErrors.email}
          aria-describedby={fieldErrors.email ? "magic-signIn-email-error" : undefined}
          disabled={loading}
        />
        {fieldErrors.email && <div id="magic-signIn-email-error" className="auth-form-error-modern" role="alert">{fieldErrors.email}</div>}
      </div>
      <button type="submit" className="auth-btn-primary-modern" disabled={loading}>
        {loading ? "Sending code..." : "Send me a code"}
      </button>
      <div className="auth-switch-text-modern">
        {t("signIn.switchText")}{" "}
        <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signUp")}>
          {t("signIn.switchLink")}
        </button>
      </div>
    </form>
  );
};

/* ===== Magic Link Sign Up Form ===== */
interface MagicLinkSignUpFormProps {
  t: (key: string) => string;
  onViewChange: (view: AuthView, email?: string) => void;
}

const generateRandomPassword = (): string => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*()_+-=';
  const all = upper + lower + digits + special;
  // Ensure at least one of each required character type
  let password = '';
  password += upper[Math.floor(Math.random() * upper.length)];
  password += lower[Math.floor(Math.random() * lower.length)];
  password += digits[Math.floor(Math.random() * digits.length)];
  password += special[Math.floor(Math.random() * special.length)];
  // Fill remaining characters
  for (let i = 4; i < 32; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

const MagicLinkSignUpForm: React.FC<MagicLinkSignUpFormProps> = ({ t, onViewChange }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<'email' | 'code'>('email');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const validateEmail = (): boolean => {
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Enter a valid email address";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setStatusMessage("");
    if (!validateEmail()) return;
    setLoading(true);
    try {
      // Sign up with random password
      const randomPassword = generateRandomPassword();
      const signUpResult = await signUp({
        username: email.trim(),
        password: randomPassword,
        options: {
          userAttributes: { email: email.trim() },
          // Pass metadata so the preSignUp Lambda auto-confirms this user
          // and suppresses Cognito's own verification email
          clientMetadata: { signUpMethod: 'passwordless' },
        },
      });

      // If Cognito requires email confirmation before sign-in, we need to
      // confirm the account first. Since we're using CUSTOM_AUTH (OTP) as
      // the verification mechanism, we use a dummy confirmation that will
      // fail, then fall through to CUSTOM_AUTH which handles unconfirmed users.
      // The DefineAuthChallenge Lambda handles CUSTOM_AUTH for unconfirmed users.
      if (!signUpResult.isSignUpComplete) {
        // User needs email confirmation — proceed to CUSTOM_AUTH anyway.
        // Cognito CUSTOM_AUTH can sign in unconfirmed users if the Lambda
        // issues tokens after successful OTP verification.
      }

      // Initiate CUSTOM_AUTH sign-in — this sends our OTP email
      const result = await signIn({ username: email.trim(), options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        setPhase('code');
        setCode("");
        setStatusMessage("A verification code has been sent to your email.");
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        // Cognito requires email confirmation before CUSTOM_AUTH can proceed.
        // Show the standard confirmation flow.
        onViewChange('confirmSignUp', email.trim());
      } else if (result.isSignedIn) {
        await fetchAuthSession();
        const { invalidateUserAttributesCache } = await import('../../utils/userAttributesCache');
        invalidateUserAttributesCache();
        await new Promise(resolve => setTimeout(resolve, 100));
        const params = new URLSearchParams(window.location.search);
        const safeRedirect = getSafeRedirect(params.get("next"));
        navigate(safeRedirect || "/app", { replace: true });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Sign up failed";
      if (message.includes("User already exists") || message.includes("UsernameExistsException")) {
        setError("An account with this email already exists. Try signing in.");
      } else if (message.includes("RATE_LIMITED")) {
        setError("Too many code requests. Please wait a few minutes and try again.");
      } else if (message.includes("UserNotConfirmedException")) {
        // User exists but not confirmed — redirect to confirmation
        onViewChange('confirmSignUp', email.trim());
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setStatusMessage("");
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        onViewChange('confirmMFA', email.trim());
      } else if (result.isSignedIn || result.nextStep?.signInStep === 'DONE') {
        try {
          await fetchAuthSession({ forceRefresh: true });
        } catch { /* ignore */ }
        const { invalidateUserAttributesCache } = await import('../../utils/userAttributesCache');
        invalidateUserAttributesCache();
        await new Promise(resolve => setTimeout(resolve, 200));
        const params = new URLSearchParams(window.location.search);
        const safeRedirect = getSafeRedirect(params.get("next"));
        navigate(safeRedirect || "/app", { replace: true });
      } else {
        console.error('Unexpected confirmSignIn result step:', result.nextStep?.signInStep);
        setError("Something went wrong. Please try again.");
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Verification failed";
      if (message.includes("Invalid") || message.includes("NotAuthorizedException") || message.includes("CodeMismatch")) {
        setError("Invalid code. Please check and try again.");
      } else if (message.includes("expired")) {
        setError("Code expired. Please request a new code.");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError("");
    setResent(false);
    setStatusMessage("");
    try {
      // Sign out first to clear any existing session, then start fresh
      try { await signOut(); } catch { /* ignore if not signed in */ }
      const result = await signIn({ username: email.trim(), options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        setCode("");
        setResent(true);
        setStatusMessage("A new code has been sent to your email. Previous codes are now invalid.");
        setTimeout(() => setResent(false), 5000);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to resend code";
      if (message.includes("RATE_LIMITED")) {
        setError("Too many code requests. Please wait a few minutes and try again.");
      } else {
        setError(message);
      }
    } finally {
      setResending(false);
    }
  };

  if (phase === 'code') {
    return (
      <form onSubmit={handleCodeSubmit} noValidate>
        {error && (
          <div className="auth-alert-modern auth-alert-error-modern" role="alert">{error}</div>
        )}
        {resent && (
          <div className="auth-alert-modern auth-alert-success-modern" role="status" aria-live="polite">
            A new code has been sent to {email}
          </div>
        )}
        {statusMessage && !resent && (
          <div role="status" aria-live="polite" className="auth-mfa-info-modern">{statusMessage}</div>
        )}
        <p className="auth-mfa-info-modern">Enter the 6-digit code sent to {email}</p>
        <CodeInputGroup value={code} onChange={setCode} id="magic-signup-code" disabled={loading} />
        <button type="submit" className="auth-btn-primary-modern" disabled={loading || code.length !== 6}>
          {loading ? "Verifying..." : "Verify & Sign Up"}
        </button>
        <div className="magic-link-resend-modern">
          <button type="button" className="resend-link-modern" onClick={handleResend} disabled={resending}>
            {resending ? "Sending..." : "Resend code"}
          </button>
        </div>
        <div className="auth-switch-text-modern">
          {t("signUp.switchText")}{" "}
          <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signIn")}>
            {t("signUp.switchLink")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleEmailSubmit} noValidate>
      {error && (
        <div className="auth-alert-modern auth-alert-error-modern" role="alert">
          {error}
          {error.includes("signing in") && (
            <>
              {" "}
              <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signIn", email.trim())}>
                Sign in
              </button>
            </>
          )}
        </div>
      )}
      <div className="auth-form-group-modern">
        <label className="auth-form-label-modern" htmlFor="magic-signUp-email">
          {t("fields.email.label")}<span className="required">*</span>
        </label>
        <input
          id="magic-signUp-email"
          className={`auth-form-input-modern${fieldErrors.email ? " error" : ""}`}
          type="email"
          placeholder={t("fields.email.placeholder")}
          value={email}
          onChange={(e) => { setEmail(e.target.value); setFieldErrors((p) => ({ ...p, email: "" })); }}
          required
          autoComplete="email"
          autoFocus
          aria-invalid={!!fieldErrors.email}
          aria-describedby={fieldErrors.email ? "magic-signUp-email-error" : undefined}
          disabled={loading}
        />
        {fieldErrors.email && <div id="magic-signUp-email-error" className="auth-form-error-modern" role="alert">{fieldErrors.email}</div>}
      </div>
      <button type="submit" className="auth-btn-primary-modern" disabled={loading}>
        {loading ? "Creating account..." : "Send me a code"}
      </button>
      <div className="auth-switch-text-modern">
        {t("signUp.switchText")}{" "}
        <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signIn")}>
          {t("signUp.switchLink")}
        </button>
      </div>
    </form>
  );
};

/* ===== Sign In Form ===== */
interface SignInFormProps {
  t: (key: string) => string;
  onViewChange: (view: AuthView, email?: string) => void;
}

const SignInForm: React.FC<SignInFormProps> = ({ t, onViewChange }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = t("fields.email.label") + " is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Enter a valid email address";
    if (!password) errs.password = t("fields.password.label") + " is required";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;
    setLoading(true);
    try {
      const result = await signIn({ username: email.trim(), password });
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        onViewChange("confirmSignUp", email.trim());
      } else if (
        result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
      ) {
        onViewChange("confirmMFA", email.trim());
      } else if (result.isSignedIn) {
        // Wait for the session to be fully established before navigating.
        await fetchAuthSession();
        console.log('[SignIn] fetchAuthSession complete, invalidating caches');

        // Invalidate caches so all components get fresh tokens on first load
        const { invalidateUserAttributesCache } = await import('../../utils/userAttributesCache');
        invalidateUserAttributesCache();

        // Small delay to ensure Amplify has fully propagated tokens internally
        // before the authenticated app mounts and starts making API calls
        console.log('[SignIn] waiting 100ms before navigate...');
        await new Promise(resolve => setTimeout(resolve, 100));
        console.log('[SignIn] navigating to /app');

        const params = new URLSearchParams(window.location.search);
        const safeRedirect = getSafeRedirect(params.get("next"));
        if (safeRedirect) {
          navigate(safeRedirect, { replace: true });
        } else {
          navigate("/app");
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Sign in failed";
      if (message.includes("User does not exist") || message.includes("Incorrect username or password")) {
        setError("Incorrect email or password. Please try again.");
      } else if (message.includes("User is not confirmed")) {
        onViewChange("confirmSignUp", email.trim());
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div className="auth-alert-modern auth-alert-error-modern" role="alert">
          {error}
        </div>
      )}
      <div className="auth-form-group-modern">
        <label className="auth-form-label-modern" htmlFor="signIn-email">
          {t("fields.email.label")}<span className="required">*</span>
        </label>
        <input
          id="signIn-email"
          className={`auth-form-input-modern${fieldErrors.email ? " error" : ""}`}
          type="email"
          placeholder={t("fields.email.placeholder")}
          value={email}
          onChange={(e) => { setEmail(e.target.value); setFieldErrors((p) => ({ ...p, email: "" })); }}
          required
          autoComplete="email"
          autoFocus
          aria-invalid={!!fieldErrors.email}
          aria-describedby={fieldErrors.email ? "signIn-email-error" : undefined}
          disabled={loading}
        />
        {fieldErrors.email && <div id="signIn-email-error" className="auth-form-error-modern" role="alert">{fieldErrors.email}</div>}
      </div>
      <PasswordField
        id="signIn-password"
        label={t("fields.password.label")}
        placeholder={t("fields.password.placeholder")}
        value={password}
        onChange={(v) => { setPassword(v); setFieldErrors((p) => ({ ...p, password: "" })); }}
        error={fieldErrors.password}
        disabled={loading}
      />
      <div className="auth-form-row-modern">
        <div />
        <button
          type="button"
          className="auth-forgot-link-modern"
          onClick={() => onViewChange("forgotPassword")}
        >
          {t("signIn.forgotPassword")}
        </button>
      </div>
      <button type="submit" className="auth-btn-primary-modern" disabled={loading}>
        {loading ? t("signIn.submitting") : t("signIn.submit")}
      </button>
      <div className="auth-switch-text-modern">
        {t("signIn.switchText")}{" "}
        <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signUp")}>
          {t("signIn.switchLink")}
        </button>
      </div>
    </form>
  );
};

/* ===== Sign Up Form ===== */
interface SignUpFormProps {
  t: (key: string) => string;
  onViewChange: (view: AuthView, email?: string) => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ t, onViewChange }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const pwChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  };
  const metCount = Object.values(pwChecks).filter(Boolean).length;
  const strengthLevel = metCount <= 1 ? "weak" : metCount <= 2 ? "fair" : metCount <= 4 ? "good" : "strong";

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!email) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Enter a valid email address";
    if (!password) errs.password = "Password is required";
    else if (password.length < 8) errs.password = "Password must be at least 8 characters";
    if (password !== confirmPw) errs.confirmPw = "Passwords do not match";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;
    setLoading(true);
    try {
      const result = await signUp({
        username: email.trim(),
        password,
        options: { userAttributes: { email: email.trim() } },
      });
      if (!result.isSignUpComplete) {
        onViewChange("confirmSignUp", email);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Sign up failed";
      if (message.includes("User already exists")) {
        setError("An account with this email already exists. Try signing in.");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div className="auth-alert-modern auth-alert-error-modern" role="alert">
          {error}
        </div>
      )}
      <div className="auth-form-group-modern">
        <label className="auth-form-label-modern" htmlFor="signUp-email">
          {t("fields.email.label")}<span className="required">*</span>
        </label>
        <input
          id="signUp-email"
          className={`auth-form-input-modern${fieldErrors.email ? " error" : ""}`}
          type="email"
          placeholder={t("fields.email.placeholder")}
          value={email}
          onChange={(e) => { setEmail(e.target.value); setFieldErrors((p) => ({ ...p, email: "" })); }}
          required
          autoComplete="email"
          autoFocus
          aria-invalid={!!fieldErrors.email}
          disabled={loading}
        />
        {fieldErrors.email && <div className="auth-form-error-modern" role="alert">{fieldErrors.email}</div>}
      </div>
      <PasswordField
        id="signUp-password"
        label={t("fields.password.label")}
        placeholder={t("fields.password.createPlaceholder")}
        value={password}
        onChange={(v) => { setPassword(v); setFieldErrors((p) => ({ ...p, password: "" })); }}
        error={fieldErrors.password}
        autoComplete="new-password"
        disabled={loading}
      />
      {password.length > 0 && (
        <div className="password-strength-modern">
          <div className="strength-bar-modern">
            <div className={`strength-fill-modern ${strengthLevel}`} />
          </div>
          <ul className="strength-reqs-modern">
            <li className={pwChecks.length ? "met" : ""}>{pwChecks.length ? "✓" : "○"} {t("passwordStrength.requirements.length")}</li>
            <li className={pwChecks.uppercase ? "met" : ""}>{pwChecks.uppercase ? "✓" : "○"} {t("passwordStrength.requirements.uppercase")}</li>
            <li className={pwChecks.lowercase ? "met" : ""}>{pwChecks.lowercase ? "✓" : "○"} {t("passwordStrength.requirements.lowercase")}</li>
            <li className={pwChecks.number ? "met" : ""}>{pwChecks.number ? "✓" : "○"} {t("passwordStrength.requirements.number")}</li>
            <li className={pwChecks.special ? "met" : ""}>{pwChecks.special ? "✓" : "○"} {t("passwordStrength.requirements.special")}</li>
          </ul>
        </div>
      )}
      <PasswordField
        id="signUp-confirm"
        label={t("fields.confirmPassword.label")}
        placeholder={t("fields.confirmPassword.placeholder")}
        value={confirmPw}
        onChange={(v) => { setConfirmPw(v); setFieldErrors((p) => ({ ...p, confirmPw: "" })); }}
        error={fieldErrors.confirmPw}
        autoComplete="new-password"
        disabled={loading}
      />
      <button type="submit" className="auth-btn-primary-modern" disabled={loading}>
        {loading ? t("signUp.submitting") : t("signUp.submit")}
      </button>
      <div className="auth-switch-text-modern">
        {t("signUp.switchText")}{" "}
        <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signIn")}>
          {t("signUp.switchLink")}
        </button>
      </div>
    </form>
  );
};

/* ===== Confirm Sign Up (Verification Code) ===== */
/* ===== Code Input Group (6 individual boxes) ===== */

interface CodeInputGroupProps {
  value: string;
  onChange: (code: string) => void;
  id?: string;
  compact?: boolean;
  disabled?: boolean;
}

const CodeInputGroup: React.FC<CodeInputGroupProps> = ({ value, onChange, id = "code", compact = false, disabled = false }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, digit: string) => {
    if (digit && !/^\d$/.test(digit)) return;
    const chars = value.padEnd(6, " ").split("");
    chars[index] = digit || " ";
    const newCode = chars.join("").replace(/ /g, "");
    onChange(newCode);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
  };

  return (
    <div className={`code-inputs-modern${compact ? " code-inputs-compact" : ""}`}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          id={i === 0 ? id : undefined}
          className={`code-input-modern${value[i] ? " filled" : ""}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          autoFocus={i === 0}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${i + 1} of 6`}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

/* ===== Confirm Sign Up Form ===== */

interface ConfirmSignUpFormProps {
  t: (key: string) => string;
  email: string;
  onViewChange: (view: AuthView) => void;
}

const ConfirmSignUpForm: React.FC<ConfirmSignUpFormProps> = ({ t, email, onViewChange }) => {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      // Auto sign-in after verification
      navigate("/login");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Verification failed";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendSignUpCode({ username: email });
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to resend code";
      setError(message);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div className="auth-alert-modern auth-alert-error-modern" role="alert">{error}</div>
      )}
      {resent && (
        <div className="auth-alert-modern auth-alert-success-modern" role="status">
          A new code has been sent to {email}
        </div>
      )}
      <CodeInputGroup value={code} onChange={setCode} id="verify-code" disabled={loading} />
      <button type="submit" className="auth-btn-primary-modern" disabled={loading || code.length !== 6}>
        {loading ? t("verify.submitting") : t("verify.submit")}
      </button>
      <div className="resend-text-modern">
        {t("verify.resendText")}{" "}
        <button type="button" className="resend-link-modern" onClick={handleResend}>
          {t("verify.resendLink")}
        </button>
      </div>
      <div className="auth-switch-text-modern">
        <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signIn")}>
          {t("verify.backToSignIn")}
        </button>
      </div>
    </form>
  );
};

/* ===== Confirm MFA (TOTP) Form ===== */
interface ConfirmMFAFormProps {
  t: (key: string) => string;
  onViewChange: (view: AuthView) => void;
}

const ConfirmMFAForm: React.FC<ConfirmMFAFormProps> = ({ t, onViewChange }) => {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.isSignedIn) {
        await fetchAuthSession();
        const params = new URLSearchParams(window.location.search);
        const safeRedirect = getSafeRedirect(params.get("next"));
        if (safeRedirect) {
          navigate(safeRedirect, { replace: true });
        } else {
          navigate("/app");
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Verification failed";
      if (message.includes("Invalid code") || message.includes("CodeMismatchException")) {
        setError("Invalid code. Please check your authenticator app and try again.");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div className="auth-alert-modern auth-alert-error-modern" role="alert">{error}</div>
      )}
      <p className="auth-mfa-info-modern">{t("mfa.infoText")}</p>
      <CodeInputGroup value={code} onChange={setCode} id="mfa-code" disabled={loading} />
      <button type="submit" className="auth-btn-primary-modern" disabled={loading || code.length !== 6}>
        {loading ? t("mfa.submitting") : t("mfa.submit")}
      </button>
      <div className="auth-switch-text-modern">
        <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signIn")}>
          {t("mfa.backToSignIn")}
        </button>
      </div>
    </form>
  );
};

/* ===== Forgot Password Form ===== */
interface ForgotPasswordFormProps {
  t: (key: string) => string;
  onViewChange: (view: AuthView, email?: string) => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ t, onViewChange }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await resetPassword({ username: email });
      onViewChange("confirmResetPassword", email);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to send reset code";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div className="auth-alert-modern auth-alert-error-modern" role="alert">{error}</div>
      )}
      <div className="auth-form-group-modern">
        <label className="auth-form-label-modern" htmlFor="forgot-email">
          {t("fields.email.label")}<span className="required">*</span>
        </label>
        <input
          id="forgot-email"
          className="auth-form-input-modern"
          type="email"
          placeholder={t("fields.email.placeholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          autoFocus
          disabled={loading}
        />
      </div>
      <button type="submit" className="auth-btn-primary-modern" disabled={loading}>
        {loading ? t("forgotPassword.submitting") : t("forgotPassword.submit")}
      </button>
      <div className="auth-switch-text-modern">
        {t("forgotPassword.switchText")}{" "}
        <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signIn")}>
          {t("forgotPassword.switchLink")}
        </button>
      </div>
    </form>
  );
};

/* ===== Confirm Reset Password Form ===== */
interface ConfirmResetPasswordFormProps {
  t: (key: string) => string;
  email: string;
  onViewChange: (view: AuthView) => void;
}

const ConfirmResetPasswordForm: React.FC<ConfirmResetPasswordFormProps> = ({ t, email, onViewChange }) => {
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPw) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      setSuccess(true);
      setTimeout(() => onViewChange("signIn"), 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Password reset failed";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ textAlign: "center", padding: "2rem 0" }}>
        <div className="auth-alert-modern auth-alert-success-modern" role="status">
          Password reset successfully. Redirecting to sign in...
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div className="auth-alert-modern auth-alert-error-modern" role="alert">{error}</div>
      )}
      <div className="auth-form-group-modern">
        <label className="auth-form-label-modern" htmlFor="reset-code">
          {t("fields.verificationCode.label")}<span className="required">*</span>
        </label>
        <CodeInputGroup value={code} onChange={setCode} id="reset-code" compact disabled={loading} />
      </div>
      <PasswordField
        id="reset-password"
        label={t("fields.newPassword.label")}
        placeholder={t("fields.newPassword.placeholder")}
        value={newPassword}
        onChange={setNewPassword}
        autoComplete="new-password"
        disabled={loading}
      />
      <PasswordField
        id="reset-confirm"
        label={t("fields.confirmNewPassword.label")}
        placeholder={t("fields.confirmNewPassword.placeholder")}
        value={confirmPw}
        onChange={setConfirmPw}
        autoComplete="new-password"
        disabled={loading}
      />
      <button type="submit" className="auth-btn-primary-modern" disabled={loading}>
        {loading ? t("resetPassword.submitting") : t("resetPassword.submit")}
      </button>
      <div className="auth-switch-text-modern">
        <button type="button" className="auth-switch-link-modern" onClick={() => onViewChange("signIn")}>
          {t("resetPassword.backToSignIn")}
        </button>
      </div>
    </form>
  );
};

/* ===== Main Auth Page ===== */
function AuthPage({ initialState = "signIn" }: AuthPageProps) {
  const { t, ready, i18n } = useTranslation("auth");
  const navigate = useNavigate();
  const [view, setView] = useState<AuthView>(initialState);
  const [email, setEmail] = useState("");
  const [authMethod, setAuthMethod] = useState<'password' | 'emailCode'>('password');

  // Subscribe-to-signup: pending package state
  const [pendingPackageName, setPendingPackageName] = useState<string | null>(null);
  const [pendingPackageLoading, setPendingPackageLoading] = useState(false);

  // On mount: check for a pending package in sessionStorage and fetch its name
  useEffect(() => {
    const pendingId = sessionStorage.getItem('pendingPackageId');
    if (!pendingId) return;

    setPendingPackageLoading(true);
    const publicClient = generateClient<Schema>({ authMode: 'apiKey' });
    publicClient.models.CommunityPackage.get({ id: pendingId })
      .then((result) => {
        if (result.data?.packageName) {
          setPendingPackageName(result.data.packageName);
        }
      })
      .catch(() => {
        // Package not found or unavailable — banner stays hidden
      })
      .finally(() => {
        setPendingPackageLoading(false);
      });
  }, []);

  // Hub listener: after successful sign-in, handle pending package addition
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      if (payload.event !== 'signedIn') return;

      const pendingId = sessionStorage.getItem('pendingPackageId');

      if (pendingId) {
        try {
          await addCommunityPackage(pendingId);
          sessionStorage.removeItem('pendingPackageId');
          navigate('/app/skillpackages', {
            replace: true,
            state: { toast: 'Package added to your profile!' },
          });
        } catch {
          // Package may no longer be available — complete signup anyway
          sessionStorage.removeItem('pendingPackageId');
          navigate('/app/skillpackages', {
            replace: true,
            state: { toast: 'Package no longer available' },
          });
        }
      } else {
        navigate('/app/skillpackages', { replace: true });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser();
        const params = new URLSearchParams(window.location.search);
        const safeRedirect = getSafeRedirect(params.get("next"));
        navigate(safeRedirect || "/app", { replace: true });
      } catch {
        // Not authenticated — stay on auth page
      }
    };
    checkAuth();
  }, [navigate]);

  // Sync view with initialState prop when route changes
  useEffect(() => {
    setView(initialState);
  }, [initialState]);

  const handleViewChange = useCallback((newView: AuthView, newEmail?: string) => {
    setView(newView);
    if (newEmail) setEmail(newEmail);
    // Update URL to match view
    if (newView === "signIn") navigate("/login", { replace: true });
    else if (newView === "signUp") navigate("/signup", { replace: true });
  }, [navigate]);

  if (!ready || !i18n.hasLoadedNamespace("auth")) {
    return null;
  }

  const getTitle = () => {
    switch (view) {
      case "signIn": return t("signIn.title");
      case "signUp": return t("signUp.title");
      case "confirmSignUp": return t("verify.title");
      case "confirmMFA": return t("mfa.title");
      case "forgotPassword": return t("forgotPassword.title");
      case "confirmResetPassword": return t("resetPassword.title");
    }
  };

  const getSubtitle = () => {
    switch (view) {
      case "signIn": return t("signIn.subtitle");
      case "signUp": return t("signUp.subtitle");
      case "confirmSignUp": return `${t("verify.subtitle")} ${email}`;
      case "confirmMFA": return t("mfa.subtitle");
      case "forgotPassword": return t("forgotPassword.subtitle");
      case "confirmResetPassword": return t("resetPassword.subtitle");
    }
  };

  // Social login: Google removed (backend migration). GitHub and Apple are coming-soon placeholders.
  const showSocial = true;

  return (
    <div className="auth-container-modern">
      <div className="auth-layout-modern">
        <AuthBrandingSection t={t} />

        <div className="auth-form-section-modern">
          {/* Mobile Header */}
          <div className="auth-mobile-header-modern">
            <img
              src={sbLogo}
              alt="SkillBubble"
              onClick={() => navigate("/")}
              style={{ cursor: "pointer" }}
            />
            <h2>{getTitle()}</h2>
            <p>{getSubtitle()}</p>
          </div>

          {/* Auth Card */}
          <div className="auth-card-modern">
            <div className="auth-card-header-modern">
              <img
                className="auth-card-logo-modern"
                src={sbLogo}
                alt="SkillBubble"
                onClick={() => navigate("/")}
              />
              <div className="auth-card-title-modern">{getTitle()}</div>
              <div className="auth-card-subtitle-modern">{getSubtitle()}</div>
            </div>

            {/* Pending package banner — shown when user arrived via Subscribe flow */}
            {(pendingPackageName || pendingPackageLoading) && (
              <div
                className="auth-pending-package-banner"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {pendingPackageLoading ? (
                  <span>Loading package details…</span>
                ) : (
                  <span>
                    You&apos;re signing up to add{' '}
                    <strong>{pendingPackageName}</strong> to your profile
                  </span>
                )}
              </div>
            )}

            {showSocial && (view === "signIn" || view === "signUp") && <SocialButtons t={t} />}

            {(view === "signIn" || view === "signUp") && (
              <AuthMethodSelector method={authMethod} onChange={setAuthMethod} />
            )}

            {view === "signIn" && authMethod === 'password' && (
              <SignInForm t={t} onViewChange={handleViewChange} />
            )}
            {view === "signIn" && authMethod === 'emailCode' && (
              <MagicLinkSignInForm t={t} onViewChange={handleViewChange} />
            )}
            {view === "signUp" && authMethod === 'password' && (
              <SignUpForm t={t} onViewChange={handleViewChange} />
            )}
            {view === "signUp" && authMethod === 'emailCode' && (
              <MagicLinkSignUpForm t={t} onViewChange={handleViewChange} />
            )}
            {view === "confirmSignUp" && (
              <ConfirmSignUpForm t={t} email={email} onViewChange={handleViewChange} />
            )}
            {view === "confirmMFA" && (
              <ConfirmMFAForm t={t} onViewChange={handleViewChange} />
            )}
            {view === "forgotPassword" && (
              <ForgotPasswordForm t={t} onViewChange={handleViewChange} />
            )}
            {view === "confirmResetPassword" && (
              <ConfirmResetPasswordForm t={t} email={email} onViewChange={handleViewChange} />
            )}
          </div>
        </div>
      </div>

      <div className="auth-footer-modern">
        {`© 2026 SkillBubble · `}
        <Link to="/terms">{t("footer.terms")}</Link>
        {` · `}
        <Link to="/privacy">{t("footer.privacy")}</Link>
        {` · `}
        <Link to="/help">{t("footer.help")}</Link>
      </div>
    </div>
  );
}

export default AuthPage;
